import { useCallback, useEffect, useRef, useState } from "react";

/**
 * Per-cell save status: idle | saving | saved | error
 * Queues overlapping saves for the same key; clears "saved" after savedDuration ms.
 */
export function useCellSaveState(savedDuration = 1200) {
  const [statuses, setStatuses] = useState({});
  const queues = useRef({});
  const timers = useRef({});
  const mounted = useRef(true);

  useEffect(() => {
    mounted.current = true;

    return () => {
      mounted.current = false;
      Object.values(timers.current).forEach(clearTimeout);
    };
  }, []);

  const setStatus = useCallback((key, status) => {
    if (!mounted.current) return;
    setStatuses((current) => ({ ...current, [key]: status }));
  }, []);

  const saveCell = useCallback(
    (key, operation) => {
      if (timers.current[key]) {
        clearTimeout(timers.current[key]);
        delete timers.current[key];
      }

      setStatus(key, "saving");

      const previous = queues.current[key] ?? Promise.resolve(null);
      const task = previous
        .catch(() => null)
        .then(operation)
        .catch(() => ({ success: false }));

      queues.current[key] = task;

      task.then((result) => {
        if (!mounted.current || queues.current[key] !== task) return;

        delete queues.current[key];
        if (!result?.success) {
          setStatus(key, "error");
          return;
        }

        setStatus(key, "saved");
        timers.current[key] = setTimeout(() => {
          if (!mounted.current) return;
          setStatuses((current) => {
            if (current[key] !== "saved") return current;

            const next = { ...current };
            delete next[key];
            return next;
          });
          delete timers.current[key];
        }, savedDuration);
      });

      return task;
    },
    [savedDuration, setStatus]
  );

  const getSaveStatus = useCallback(
    (key) => statuses[key] ?? "idle",
    [statuses]
  );

  return { getSaveStatus, saveCell };
}
